import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, UserCog, Wrench, Hammer, Calculator, PackageSearch, ShoppingCart, Home, Copy, Play, Users2, ArrowRight, MonitorSmartphone, Sparkles } from 'lucide-react';
import { setSessionToken } from '../App';
import BrandLogo from '../components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  FULL_ACCESS: Shield,
  MANAGER: UserCog,
  ENGINEER: Wrench,
  TECHNICIAN: Hammer,
  ACCOUNTANT: Calculator,
  STOREKEEPER: PackageSearch,
  PROCUREMENT_OFFICER: ShoppingCart,
  TENANT: Home,
};

const ROLE_ACCENT = {
  FULL_ACCESS: 'from-teal-500 to-cyan-500',
  MANAGER: 'from-indigo-500 to-blue-500',
  ENGINEER: 'from-amber-500 to-orange-500',
  TECHNICIAN: 'from-orange-500 to-red-500',
  ACCOUNTANT: 'from-emerald-500 to-green-500',
  STOREKEEPER: 'from-purple-500 to-pink-500',
  PROCUREMENT_OFFICER: 'from-rose-500 to-pink-500',
  TENANT: 'from-sky-500 to-blue-500',
};

const STORY_STEPS = [
  { role: 'TENANT',              text: 'Tenant raises a complaint from unit 1204 with photo — status: Open.' },
  { role: 'ENGINEER',            text: 'Engineer opens it, assigns to Technician, creates a work order.' },
  { role: 'TECHNICIAN',          text: 'Technician scans asset QR, requests material (e.g. 2m PVC pipe).' },
  { role: 'STOREKEEPER',         text: 'Storekeeper checks stock — if in-stock, issues material; stock reduces automatically.' },
  { role: 'PROCUREMENT_OFFICER', text: 'If stock is short, Purchaser raises PR → LPO → sends to supplier → GRN receipt.' },
  { role: 'STOREKEEPER',         text: 'GRN increases stock, Storekeeper issues to Technician.' },
  { role: 'TECHNICIAN',          text: 'Technician fixes the issue, files service report with photos, marks work completed.' },
  { role: 'ENGINEER',            text: 'Engineer verifies, closes complaint. Tenant notified.' },
  { role: 'STOREKEEPER',         text: 'Returnable items (e.g. loan tools) tracked — pending returns dashboard by technician.' },
  { role: 'MANAGER',             text: 'Manager approves the LPO / voucher / snag cost inside the Approvals Hub.' },
  { role: 'ACCOUNTANT',          text: 'Accountant posts voucher → GL auto-posts double-entry → Balance Sheet & P&L update live.' },
  { role: 'TENANT',              text: 'Tenant portal shows complaint history + snag deductions from deposit if any.' },
];

export default function DemoTour() {
  const [roles, setRoles] = useState([]);
  const [password, setPassword] = useState('DemoWalk2026!');
  const [companyName, setCompanyName] = useState('SKG Demo · Bay Square Tower');
  const [loading, setLoading] = useState(true);
  const [busyRole, setBusyRole] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/demo/roles`)
      .then(r => {
        setRoles(r.data.roles || []);
        setPassword(r.data.password);
        setCompanyName(r.data.company_name);
      })
      .catch(() => toast.error('Demo not available yet. Please try again in a moment.'))
      .finally(() => setLoading(false));
  }, []);

  const loginAs = async (role) => {
    setBusyRole(role);
    try {
      const res = await axios.post(`${API}/auth/demo-walkthrough`, { role }, { withCredentials: true });
      if (res.data?.session_token) setSessionToken(res.data.session_token);
      toast.success(`Signed in as ${res.data.label}. Watch changes flow to other roles live.`);
      // Reload so AuthProvider re-fetches /auth/me with the new session.
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Demo login failed');
    } finally {
      setBusyRole(null);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/demo-tour`;
    navigator.clipboard.writeText(link);
    toast.success('Demo link copied — share it with your client.');
  };

  const copyCreds = (email) => {
    navigator.clipboard.writeText(`${email} / ${password}`);
    toast.success('Credentials copied.');
  };

  return (
    <div data-testid="demo-tour-page" className="min-h-screen bg-[#0a0f1e] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0f1e]/85 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center gap-3">
          <a href="/" className="flex items-center gap-3">
            <BrandLogo variant="icon" size={44} className="rounded-xl shadow" />
            <span className="text-sm uppercase tracking-[0.25em] text-slate-400 hidden sm:block">Live Walk-Through</span>
          </a>
          <button
            data-testid="copy-demo-link-btn"
            onClick={copyLink}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/40 text-teal-300 hover:bg-teal-500/20 text-sm transition"
          >
            <Copy className="w-4 h-4" /> Copy Demo Link
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 pb-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" /> Client Walk-Through Mode
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
            Watch every FM role work together, <span className="text-teal-400">live</span>.
          </h1>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
            One demo tenant. Eight role logins. Open this link on multiple PCs, pick a different role on each,
            and watch complaints, work orders, material issues, POs, GRNs, snag deductions and journal entries
            sync in real time across every screen.
          </p>
          <p className="text-sm text-slate-400 mt-3">
            Demo tenant: <span className="text-white font-semibold">{companyName}</span>
            <span className="mx-2">·</span>
            Password for every role: <code className="bg-slate-800 px-2 py-0.5 rounded text-teal-300">{password}</code>
          </p>
        </div>
      </section>

      {/* Role picker */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-teal-400 rounded" />
          <h2 className="text-2xl font-bold">Pick a role to log in as</h2>
        </div>

        {loading ? (
          <div className="animate-pulse text-slate-400">Loading demo roles…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {roles.map((r) => {
              const Icon = ROLE_ICONS[r.role] || Users2;
              const accent = ROLE_ACCENT[r.role] || 'from-slate-500 to-slate-600';
              return (
                <div
                  key={r.role}
                  data-testid={`demo-role-card-${r.role}`}
                  className="group relative rounded-2xl border border-slate-800 bg-slate-900/50 hover:border-teal-500/60 transition overflow-hidden"
                >
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">{r.role}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">{r.label}</h3>
                    <p className="text-xs text-slate-500 mb-3">{r.name}</p>
                    <p className="text-sm text-slate-300 mb-4 min-h-[3rem]">{r.tagline}</p>

                    <ul className="text-xs text-slate-400 space-y-1 mb-4">
                      {(r.workflow || []).map((w, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-teal-400 flex-shrink-0">▸</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      data-testid={`demo-login-btn-${r.role}`}
                      onClick={() => loginAs(r.role)}
                      disabled={busyRole === r.role}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r ${accent} text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50 transition`}
                    >
                      {busyRole === r.role ? 'Signing in…' : <><Play className="w-4 h-4" /> Log in as {r.label}</>}
                    </button>

                    <button
                      onClick={() => copyCreds(r.email)}
                      className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-1.5 text-xs text-slate-400 hover:text-teal-300 border border-slate-800 hover:border-teal-500/40 rounded-lg transition"
                    >
                      <Copy className="w-3 h-3" /> Copy email + password
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Multi-PC guide */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
        <div className="rounded-2xl border border-teal-900/40 bg-gradient-to-br from-teal-500/5 to-cyan-500/5 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-500/40 flex items-center justify-center flex-shrink-0">
              <MonitorSmartphone className="w-6 h-6 text-teal-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">How to run a multi-PC live demo</h2>
              <ol className="text-sm text-slate-300 space-y-2 list-decimal pl-4">
                <li>Share the demo link with your client (already copied to clipboard when you clicked Copy Demo Link).</li>
                <li>Open the link on 2–8 devices (or 2 browsers on the same PC — Chrome + Firefox works fine).</li>
                <li>On each device, pick a different role card above.</li>
                <li>Start the story: Tenant raises a complaint. Watch the Engineer&apos;s dashboard receive it within seconds.</li>
                <li>Play the storyboard below on-screen while you drive the demo.</li>
              </ol>
              <p className="text-xs text-slate-500 mt-3">Tip: sessions last 8 hours. Any changes made stay inside the demo tenant only — your production data is untouched.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Storyboard */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-amber-400 rounded" />
          <h2 className="text-2xl font-bold">End-to-end scenario</h2>
        </div>
        <div className="space-y-3">
          {STORY_STEPS.map((s, i) => {
            const Icon = ROLE_ICONS[s.role] || Users2;
            const accent = ROLE_ACCENT[s.role] || 'from-slate-500 to-slate-600';
            const label = (roles.find(r => r.role === s.role) || {}).label || s.role;
            return (
              <div key={i} data-testid={`story-step-${i + 1}`} className="flex items-start gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/40 hover:border-teal-500/40 transition">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-teal-300 border border-slate-700">
                  {i + 1}
                </div>
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
                  <div className="text-sm text-slate-200">{s.text}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 mt-3 hidden sm:block" />
              </div>
            );
          })}
        </div>
      </section>

      {/* Feature highlights */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-purple-400 rounded" />
          <h2 className="text-2xl font-bold">What to demo next</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { t: 'Repeated complaints', d: 'Same tenant, same asset — the system auto-flags recurring issues so root-cause can be addressed.' },
            { t: 'Returnable material', d: 'Loan tools stay on a technician&apos;s open tab until returned. See who owes what, aging by days.' },
            { t: 'Snag with pictures', d: 'Tenant check-out inspection with photos, cost split between tenant & landlord, deposit adjustment.' },
            { t: 'GL auto-post', d: 'Approved vouchers & credit/debit notes auto-create double-entry journal entries — BS & P&L stay in sync.' },
            { t: 'Approvals Hub', d: 'One centralized queue for POs, vouchers, snag costs, service reports — full audit trail.' },
            { t: 'Per-party statements', d: 'Tenant / supplier / client statements with running balances, printable branded PDF.' },
          ].map((f, i) => (
            <div key={i} className="p-5 rounded-xl border border-slate-800 bg-slate-900/40">
              <div className="text-teal-300 font-bold mb-1">{f.t}</div>
              <div className="text-sm text-slate-400">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
        © 2026 SKG Apps · FM Flexi Pro™ · <a href="/" className="hover:text-teal-400">Back to home</a>
      </footer>
    </div>
  );
}
